import { Pencil } from "lucide-react";
import { useEffect, useRef, useState, type JSX, type KeyboardEvent } from "react";

import { cn } from "@/shared/lib/cn";

interface InlineEditFieldProps {
  value: string;
  onSave: (next: string) => void;
  className?: string;
  format?: (v: string) => string;
}

/**
 * Plain at rest + always-visible faint pencil (familiar, not boxed); hover tints;
 * click → clean inline input. Enter saves, Esc cancels.
 */
export function InlineEditField({
  value,
  onSave,
  className,
  format,
}: InlineEditFieldProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  if (editing) {
    const commit = (): void => {
      onSave(draft);
      setEditing(false);
    };
    return (
      <input
        ref={inputRef}
        value={draft}
        data-testid="inline-edit-input"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setDraft(value);
            setEditing(false);
          }
        }}
        className="num h-7 w-[72px] rounded-md border border-[var(--brand-500)] px-2 text-right text-sm outline-none"
        style={{ boxShadow: "0 0 0 3px rgba(15,98,254,.16)" }}
      />
    );
  }

  return (
    <button
      type="button"
      data-testid="inline-edit"
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      className={cn(
        "num group -mx-2 inline-flex items-center justify-end gap-1.5 rounded-md px-2 py-1 transition hover:bg-[var(--brand-50)]",
        className,
      )}
    >
      {format ? format(value) : value}
      <Pencil
        aria-hidden
        className="h-3 w-3 text-[var(--text-subtle)] opacity-40 transition group-hover:text-[var(--brand-500)] group-hover:opacity-100"
      />
    </button>
  );
}
